/** @jsxRuntime automatic */
/* eslint-disable react-refresh/only-export-components -- Exports provider, store, and hooks */
/**
 * Tax "R" Us Clara Intelligence — App Store
 * React Context + useReducer based state management for navigation and panels
 */

import type { ReactNode, Dispatch } from 'react';
import { createContext, useContext, useReducer, useCallback } from 'react';
import type { AppState, AppAction, NavigationState, PanelState, NotificationState, UtilityPanelMode, UserProfileState, UserProfileData, UserProfileError } from '../types';
import { CONFIG } from '../constants';

const APP_ID_ALIASES: Record<string, string> = {
  'tru-clara-ai': CONFIG.CLARA_APP,
  'tru-clara-app': CONFIG.CLARA_APP,
  'kci-workspaces': CONFIG.WORKSPACE,
  'kci-agents': CONFIG.AGENTS,
};

const normalizeAppId = (appId: string): string => APP_ID_ALIASES[appId] ?? appId;

export const buildWorkspaceContainerToken = (app: string, page: string, subPage: string | null) =>
  `workspace:${app}:${page}:${subPage ?? 'default'}`;

const initialNavigationState: NavigationState = {
  currentApp: 'tru-clara-app',
  currentPage: '__clara-ai-chat__',
  currentSubPage: null,
  workspaceContainerTokens: [buildWorkspaceContainerToken('tru-clara-app', '__clara-ai-chat__', null)],
  currentLevel: 'app',
  inSubNav: false,
  panelApp: 'tru-clara-app',
  panelPage: null,
  panelIsOpen: false,
  panelIsPinned: false,
  activeEngagementId: null,
  activeFirmId: 'firm-us',
};

const initialPanelState: PanelState = {
  utilityPanelOpen: false,
  utilityPanelPinned: false,
  utilityPanelMode: null,
  aiPanelOpen: false,
  teamsPanelOpen: false,
  reasoningPanelOpen: false,
  currentPanel: null,
};

const initialNotificationState: NotificationState = {
  items: [],
  loading: false,
  lastFetchedApp: null,
  error: null,
};

const initialUserProfileState: UserProfileState = {
  data: null,
  loading: false,
  lastFetchedAt: null,
  accountKey: null,
  error: null,
};

const initialState: AppState = {
  navigation: initialNavigationState,
  panels: initialPanelState,
  notifications: initialNotificationState,
  userProfile: initialUserProfileState,
};

function navigationReducer(state: NavigationState, action: AppAction): NavigationState {
  switch (action.type) {
    case 'NAVIGATE_TO_PAGE': {
      const normalizedApp = normalizeAppId(action.payload.app);
      const normalizedSubPage = action.payload.subPage || null;
      const nextWorkspaceToken = buildWorkspaceContainerToken(
        normalizedApp,
        action.payload.page,
        normalizedSubPage
      );

      if (
        state.currentApp === normalizedApp &&
        state.currentPage === action.payload.page &&
        state.currentSubPage === normalizedSubPage &&
        state.workspaceContainerTokens.includes(nextWorkspaceToken)
      ) {
        return state;
      }

      return {
        ...state,
        currentApp: normalizedApp,
        currentPage: action.payload.page,
        currentSubPage: normalizedSubPage,
        workspaceContainerTokens: state.workspaceContainerTokens.includes(nextWorkspaceToken)
          ? state.workspaceContainerTokens
          : [...state.workspaceContainerTokens, nextWorkspaceToken],
      };
    }

    case 'SWITCH_APP':
      return {
        ...state,
        currentApp: normalizeAppId(action.payload.appId),
        panelApp: normalizeAppId(action.payload.appId),
        currentLevel: 'app',
      };

    case 'SET_CURRENT_LEVEL':
      return {
        ...state,
        currentLevel: action.payload.level,
      };

    case 'SET_SUB_NAV':
      return {
        ...state,
        inSubNav: action.payload.inSubNav,
      };

    case 'SET_PANEL_APP':
      return {
        ...state,
        panelApp: normalizeAppId(action.payload.appId),
      };

    case 'SET_PANEL_PAGE':
      return {
        ...state,
        panelPage: action.payload.pageId,
      };

    case 'TOGGLE_PANEL':
      return {
        ...state,
        panelIsOpen: action.payload.open,
      };

    case 'TOGGLE_PIN':
      return {
        ...state,
        panelIsPinned: action.payload.pinned,
      };

    case 'SET_ENGAGEMENT':
      return {
        ...state,
        activeEngagementId: action.payload.engagementId,
      };

    case 'SET_FIRM':
      return {
        ...state,
        activeFirmId: action.payload.firmId,
        activeEngagementId: null,
      };

    case 'REMOVE_WORKSPACE_CONTAINER_TOKEN': {
      if (!state.workspaceContainerTokens.includes(action.payload.token)) {
        return state;
      }
      return {
        ...state,
        workspaceContainerTokens: state.workspaceContainerTokens.filter(
          (token) => token !== action.payload.token
        ),
      };
    }

    default:
      return state;
  }
}

function panelReducer(state: PanelState, action: AppAction): PanelState {
  switch (action.type) {
    case 'TOGGLE_UTILITY_PANEL':
      return {
        ...state,
        utilityPanelOpen: action.payload.open,
        // Clear mode when closing so re-open lands on a fresh state.
        utilityPanelMode: action.payload.open ? state.utilityPanelMode : null,
      };

    case 'SET_UTILITY_PANEL_MODE':
      return {
        ...state,
        utilityPanelMode: action.payload.mode,
        utilityPanelOpen: action.payload.mode !== null,
      };

    case 'TOGGLE_REASONING_PANEL':
      return {
        ...state,
        reasoningPanelOpen: action.payload.open,
      };

    case 'CLOSE_ALL_PANELS':
      return {
        ...state,
        utilityPanelOpen: false,
        utilityPanelMode: null,
        aiPanelOpen: false,
        teamsPanelOpen: false,
        reasoningPanelOpen: false,
        currentPanel: null,
      };

    case 'TOGGLE_AI_PANEL':
      return {
        ...state,
        aiPanelOpen: action.payload.open,
      };

    case 'TOGGLE_TEAMS_PANEL':
      return {
        ...state,
        teamsPanelOpen: action.payload.open,
      };

    case 'SET_CURRENT_PANEL':
      return {
        ...state,
        currentPanel: action.payload.panel as PanelState['currentPanel'],
      };

    case 'PIN_UTILITY_PANEL':
      return {
        ...state,
        utilityPanelPinned: action.payload.pinned,
      };

    case 'PIN_AI_PANEL':
      // Note: AI panel pinning would be handled similarly
      return state;

    default:
      return state;
  }
}

function notificationReducer(state: NotificationState, action: AppAction): NotificationState {
  switch (action.type) {
    case 'SET_NOTIFICATIONS':
      return { ...state, items: action.payload.items, loading: false, error: null };
    case 'MARK_NOTIFICATION_READ':
      return {
        ...state,
        items: state.items.map(n =>
          n.id === action.payload.id ? { ...n, read: action.payload.read } : n
        ),
      };
    case 'MARK_NOTIFICATION_UNREAD':
      return {
        ...state,
        items: state.items.map(n =>
          n.id === action.payload.id ? { ...n, read: false } : n
        ),
      };
    case 'DELETE_NOTIFICATION':
      return {
        ...state,
        items: state.items.filter(n => n.id !== action.payload.id),
      };
    case 'MARK_ALL_NOTIFICATIONS_READ':
      return {
        ...state,
        items: state.items.map(n => ({ ...n, read: true })),
      };
    case 'DELETE_ALL_NOTIFICATIONS':
      return { ...state, items: [] };
    case 'SET_NOTIFICATIONS_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload.error
          ? { message: action.payload.error, timestamp: Date.now() }
          : null,
      };
    case 'SET_NOTIFICATIONS_LOADING':
      return { ...state, loading: action.payload.loading };
    case 'SET_LAST_FETCHED_APP':
      return { ...state, lastFetchedApp: action.payload.appId };
    default:
      return state;
  }
}

function userProfileReducer(state: UserProfileState, action: AppAction): UserProfileState {
  switch (action.type) {
    case 'SET_USER_PROFILE_LOADING':
      return { ...state, loading: action.payload.loading, accountKey: action.payload.accountKey };
    case 'SET_USER_PROFILE_SUCCESS':
      return {
        ...state,
        data: action.payload.data,
        loading: false,
        error: null,
        lastFetchedAt: action.payload.fetchedAt,
        accountKey: action.payload.accountKey,
      };
    case 'SET_USER_PROFILE_ERROR':
      return { ...state, loading: false, error: action.payload.error, accountKey: action.payload.accountKey };
    case 'CLEAR_USER_PROFILE_ERROR':
      return { ...state, error: null };
    case 'RESET_USER_PROFILE':
      return initialUserProfileState;
    default:
      return state;
  }
}

function appReducer(state: AppState, action: AppAction): AppState {
  return {
    navigation: navigationReducer(state.navigation, action),
    panels: panelReducer(state.panels, action),
    notifications: notificationReducer(state.notifications, action),
    userProfile: userProfileReducer(state.userProfile, action),
  };
}

interface AppContextType {
  state: AppState;
  dispatch: Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppStore = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppStore must be used within an AppProvider');
  }
  return context;
};

// Convenience hooks for common actions

export const useNavigation = () => {
  const { state, dispatch } = useAppStore();
  const navigateToPage = useCallback((app: string, page: string, subPage?: string | null) => {
    dispatch({
      type: 'NAVIGATE_TO_PAGE',
      payload: { app, page, subPage },
    });
  }, [dispatch]);

  const removeWorkspaceContainerToken = useCallback((token: string) => {
    dispatch({
      type: 'REMOVE_WORKSPACE_CONTAINER_TOKEN',
      payload: { token },
    });
  }, [dispatch]);

  return {
    navigation: state.navigation,
    navigateToPage,
    removeWorkspaceContainerToken,
    switchApp: (appId: string) => {
      dispatch({
        type: 'SWITCH_APP',
        payload: { appId },
      });
    },
    setCurrentLevel: (level: 'waffle' | 'app' | 'page') => {
      dispatch({
        type: 'SET_CURRENT_LEVEL',
        payload: { level },
      });
    },
    setSubNav: (inSubNav: boolean) => {
      dispatch({
        type: 'SET_SUB_NAV',
        payload: { inSubNav },
      });
    },
    setPanelApp: (appId: string) => {
      dispatch({
        type: 'SET_PANEL_APP',
        payload: { appId },
      });
    },
    setPanelPage: (pageId: string | null) => {
      dispatch({
        type: 'SET_PANEL_PAGE',
        payload: { pageId },
      });
    },
    togglePanel: (open: boolean) => {
      dispatch({
        type: 'TOGGLE_PANEL',
        payload: { open },
      });
    },
    togglePin: (pinned: boolean) => {
      dispatch({
        type: 'TOGGLE_PIN',
        payload: { pinned },
      });
    },
  };
};

export const usePanels = () => {
  const { state, dispatch } = useAppStore();

  const toggleUtilityPanel = useCallback(
    (open: boolean) => {
      dispatch({
        type: 'TOGGLE_UTILITY_PANEL',
        payload: { open },
      });
    },
    [dispatch]
  );

  const toggleAIPanel = useCallback(
    (open: boolean) => {
      dispatch({
        type: 'TOGGLE_AI_PANEL',
        payload: { open },
      });
    },
    [dispatch]
  );

  const toggleTeamsPanel = useCallback(
    (open: boolean) => {
      dispatch({
        type: 'TOGGLE_TEAMS_PANEL',
        payload: { open },
      });
    },
    [dispatch]
  );

  const setCurrentPanel = useCallback(
    (panel: 'utility' | 'ai' | 'teams' | 'reasoning' | null) => {
      dispatch({
        type: 'SET_CURRENT_PANEL',
        payload: { panel },
      });
    },
    [dispatch]
  );

  const pinUtilityPanel = useCallback(
    (pinned: boolean) => {
      dispatch({
        type: 'PIN_UTILITY_PANEL',
        payload: { pinned },
      });
    },
    [dispatch]
  );

  const setUtilityPanelMode = useCallback(
    (mode: UtilityPanelMode) => {
      dispatch({ type: 'SET_UTILITY_PANEL_MODE', payload: { mode } });
    },
    [dispatch]
  );

  const toggleReasoningPanel = useCallback(
    (open: boolean) => {
      dispatch({ type: 'TOGGLE_REASONING_PANEL', payload: { open } });
    },
    [dispatch]
  );

  const closeAllPanels = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_PANELS' });
  }, [dispatch]);

  return {
    panels: state.panels,
    toggleUtilityPanel,
    toggleAIPanel,
    toggleTeamsPanel,
    setCurrentPanel,
    pinUtilityPanel,
    setUtilityPanelMode,
    toggleReasoningPanel,
    closeAllPanels,
  };
};

export const useEngagement = () => {
  const { state, dispatch } = useAppStore();
  return {
    activeEngagementId: state.navigation.activeEngagementId,
    activeFirmId: state.navigation.activeFirmId,
    setEngagement: useCallback(
      (engId: string | null) =>
        dispatch({ type: 'SET_ENGAGEMENT', payload: { engagementId: engId } }),
      [dispatch]
    ),
    setFirm: useCallback(
      (firmId: string) =>
        dispatch({ type: 'SET_FIRM', payload: { firmId } }),
      [dispatch]
    ),
  };
};

export const useUserProfileState = () => {
  const { state, dispatch } = useAppStore();

  const setLoading = useCallback(
    (loading: boolean, accountKey: string | null) => {
      dispatch({ type: 'SET_USER_PROFILE_LOADING', payload: { loading, accountKey } });
    },
    [dispatch]
  );

  const setSuccess = useCallback(
    (data: UserProfileData, accountKey: string, fetchedAt: number) => {
      dispatch({ type: 'SET_USER_PROFILE_SUCCESS', payload: { data, accountKey, fetchedAt } });
    },
    [dispatch]
  );

  const setError = useCallback(
    (error: UserProfileError, accountKey: string | null) => {
      dispatch({ type: 'SET_USER_PROFILE_ERROR', payload: { error, accountKey } });
    },
    [dispatch]
  );

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_USER_PROFILE_ERROR' });
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET_USER_PROFILE' });
  }, [dispatch]);

  return { userProfile: state.userProfile, setLoading, setSuccess, setError, clearError, reset };
};
